import React, { useRef, useState } from 'react';
import {
  captureExternalAppEvidence,
  getExternalAppEvidenceScreenshot,
  saveExternalAppPlaybook,
} from './externalAppControlApi';
import ScreenshotRegionSelector from './ScreenshotRegionSelector';
import { buildVisualTemplateFromImage } from './visualTemplateSelection';

const ACTIONS = ['home', 'back', 'up', 'down', 'left', 'right', 'enter', 'menu', 'info'];

export const slugStateId = (value) => String(value || '')
  .trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80);

function ScreenStateTrainer({
  session,
  playbook,
  controlTransport,
  onRemoteAction,
  onSaved,
  onMessage,
  onError,
}) {
  const imageRef = useRef(null);
  const [capture, setCapture] = useState(null);
  const [selection, setSelection] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState('navigable');
  const [destination, setDestination] = useState(false);
  const [fromStateId, setFromStateId] = useState('');
  const [toStateId, setToStateId] = useState('');
  const [steps, setSteps] = useState([]);
  const [busy, setBusy] = useState('');

  const captureScreen = async () => {
    setBusy('capture');
    try {
      const evidence = await captureExternalAppEvidence(session.id, true);
      const screenshot = await getExternalAppEvidenceScreenshot(session.id, evidence.evidenceId);
      setCapture({ evidenceId: evidence.evidenceId, dataUrl: screenshot.dataUrl });
      setSelection(null);
      setDrawing(false);
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Could not capture the current screen.');
    } finally {
      setBusy('');
    }
  };

  const saveScreen = async () => {
    const id = slugStateId(label);
    if (!id || !capture || !selection) {
      onError('Enter a screen name, capture it, and draw a stable identifying region.');
      return;
    }
    if ((playbook.states || []).some((state) => state.id === id)) {
      onError('A screen with this name already exists. Use a distinct name.');
      return;
    }
    setBusy('screen');
    try {
      const template = buildVisualTemplateFromImage(imageRef.current, selection, 0.86);
      const next = {
        ...playbook,
        states: [...(playbook.states || []), {
          id,
          label: label.trim(),
          kind,
          recognisers: [{
            id: `${id}-visual`,
            type: 'visual_template',
            required: true,
            weight: 100,
            minimumConfidence: 0.8,
            detector: { template },
          }],
        }],
        destinations: destination ? [...(playbook.destinations || []), {
          id,
          label: label.trim(),
          stateId: id,
        }] : playbook.destinations || [],
        calibration: {
          ...(playbook.calibration || {}),
          status: 'trained_here',
          requiresVisualRevalidation: false,
          validatedAt: new Date().toISOString(),
        },
      };
      const saved = await saveExternalAppPlaybook(session.id, next);
      onSaved(saved);
      setLabel('');
      setCapture(null);
      setSelection(null);
      onMessage(`${label.trim()} is now a known screen${destination ? ' and destination' : ''}.`);
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Could not save this screen.');
    } finally {
      setBusy('');
    }
  };

  const sendAndRecord = async (action) => {
    setBusy(`action:${action}`);
    try {
      await onRemoteAction(action);
      setSteps((current) => [...current, {
        action,
        transport: controlTransport,
        delayAfterMs: 500,
      }]);
      onMessage(`${action.replace(/_/g, ' ')} sent and added to this route.`);
    } catch (error) {
      onError(error.response?.data?.message || error.message || 'Remote action failed.');
    } finally {
      setBusy('');
    }
  };

  const saveRoute = async () => {
    if (!fromStateId || !toStateId || !steps.length) {
      onError('Choose the starting and resulting screens, then record at least one action.');
      return;
    }
    setBusy('route');
    try {
      const routeNumber = (playbook.transitions || []).length + 1;
      const saved = await saveExternalAppPlaybook(session.id, {
        ...playbook,
        transitions: [...(playbook.transitions || []), {
          id: `route-${fromStateId}-${toStateId}-${routeNumber}`,
          label: `${fromStateId} to ${toStateId}`,
          fromStateIds: [fromStateId],
          toStateIds: [toStateId],
          actions: steps,
          retrySafe: false,
          safety: { maxAttempts: 1, timeoutMs: 60000, maxKeypresses: steps.length },
        }],
      });
      onSaved(saved);
      setSteps([]);
      onMessage('Navigation route saved. Runtime will verify the resulting screen before continuing.');
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Could not save this route.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="apps-launch-recipe__grid">
      <section>
        <strong>1. Teach the screen currently on the TV</strong>
        <p>Name its meaning, capture it, then draw around a stable heading, icon, or menu element.</p>
        <label>Screen name <input value={label} onChange={(event) => setLabel(event.target.value)}
          placeholder="EPG, Home, Exit confirmation…" /></label>
        <label>Meaning <select value={kind} onChange={(event) => setKind(event.target.value)}>
          <option value="navigable">Useful milestone</option><option value="destination">Destination</option>
          <option value="playback">Playing video</option><option value="obstacle">Interruption</option>
          <option value="error">Error</option><option value="transient">Loading/transient</option>
        </select></label>
        <label><input type="checkbox" checked={destination}
          onChange={(event) => setDestination(event.target.checked)} /> Allow operations to request this screen</label>
        <div className="apps-launch-recipe__heading-actions">
          <button type="button" onClick={captureScreen} disabled={Boolean(busy)}>
            {busy === 'capture' ? 'Capturing…' : 'Capture current screen'}
          </button>
          {capture && <button type="button" className={drawing ? 'primary' : ''}
            onClick={() => setDrawing(true)}>Draw identifying region</button>}
        </div>
        {capture && <ScreenshotRegionSelector dataUrl={capture.dataUrl} alt="Current app screen"
          imageRef={imageRef} active={drawing} onRegion={(region) => { setSelection(region); setDrawing(false); }}
          regions={[{ key: 'identifier', region: selection, colour: '#22c55e' }]} />}
        {capture && <button type="button" className="primary" onClick={saveScreen} disabled={Boolean(busy)}>
          Save known screen
        </button>}
      </section>
      <section>
        <strong>2. Record how to move between known screens</strong>
        <p>Place the TV on the starting screen. Each button is sent through {controlTransport.replace(/_/g, ' ')} and recorded.</p>
        <label>From <select value={fromStateId} onChange={(event) => setFromStateId(event.target.value)}>
          <option value="">Choose screen…</option>{(playbook.states || []).map((state) => <option key={state.id} value={state.id}>{state.label}</option>)}
        </select></label>
        <label>To <select value={toStateId} onChange={(event) => setToStateId(event.target.value)}>
          <option value="">Choose screen…</option>{(playbook.states || []).map((state) => <option key={state.id} value={state.id}>{state.label}</option>)}
        </select></label>
        <div className="apps-launch-recipe__heading-actions">
          {ACTIONS.map((action) => <button type="button" key={action} onClick={() => sendAndRecord(action)}
            disabled={Boolean(busy)}>{action.replace(/_/g, ' ')}</button>)}
        </div>
        <p>{steps.length ? steps.map((step) => step.action).join(' → ') : 'No actions recorded yet.'}</p>
        <button type="button" onClick={() => setSteps((current) => current.slice(0, -1))} disabled={!steps.length}>Undo last</button>
        <button type="button" className="primary" onClick={saveRoute} disabled={Boolean(busy) || !steps.length}>Save route</button>
      </section>
    </div>
  );
}

export default ScreenStateTrainer;
